import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import freeTrialManager from '../services/auth/freeTrialManager';

/**
 * Custom hook for managing review logic
 * @param {Object} options - Configuration options
 * @param {string} options.type - Test type (viajeros, mercancias)
 */
const useReviewLogic = ({ type }) => {
  const [preguntasFalladas, setPreguntasFalladas] = useState([]);
  const [preguntaActual, setPreguntaActual] = useState(0);
  const [respuestas, setRespuestas] = useState({});
  const [modoRepaso, setModoRepaso] = useState(false);
  const [userStatus, setUserStatus] = useState(null);
  const [showTrialLimitModal, setShowTrialLimitModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Fetch user status
  useEffect(() => {
    let mounted = true;

    const fetchUserStatus = async () => {
      try {
        const status = await freeTrialManager.getUserStatus();
        if (mounted) {
          setUserStatus(status);
        }
      } catch (err) {
        console.error('Error fetching user status:', err);
        if (mounted) {
          setUserStatus({ type: 'trial', testsAvailable: 0 });
        }
      }
    };

    fetchUserStatus();

    return () => {
      mounted = false;
    };
  }, []);

  // Load failed questions
  useEffect(() => {
    // Check if user has access to review
    if (!isAuthenticated) {
      setShowTrialLimitModal(true);
      return;
    }

    const cargarPreguntasFalladas = async () => {
      try {
        // Load failed questions from localStorage
        const falladas = localStorage.getItem('preguntasFalladas');
        if (falladas) {
          const todasLasFalladas = JSON.parse(falladas);
          // Filtrar preguntas por tipo (comun + tipo específico)
          const preguntasFiltradas = todasLasFalladas.filter(pregunta => 
            pregunta.moduloId === 'comun' || pregunta.moduloId === type
          );
          setPreguntasFalladas(preguntasFiltradas);
        }
      } catch (error) {
        console.error('Error al cargar preguntas falladas:', error);
      }
    };

    cargarPreguntasFalladas();
  }, [isAuthenticated, navigate, type]);

  // Handle answer selection
  const handleRespuesta = useCallback((opcionIndex) => {
    const preguntaActualObj = preguntasFalladas[preguntaActual];
    if (!preguntaActualObj) return;
    
    const esCorrecta = opcionIndex === preguntaActualObj.respuestaCorrecta;

    setRespuestas(prev => ({
      ...prev,
      [preguntaActual]: {
        seleccionada: opcionIndex,
        correcta: esCorrecta
      }
    }));

    if (esCorrecta) {
      setTimeout(() => {
        // Obtener todas las preguntas falladas
        const todasLasFalladas = JSON.parse(localStorage.getItem('preguntasFalladas') || '[]');
        // Eliminar la pregunta actual
        const nuevasFalladas = todasLasFalladas.filter(p => p.id !== preguntaActualObj.id);
        localStorage.setItem('preguntasFalladas', JSON.stringify(nuevasFalladas));
        
        // Actualizar el estado con las preguntas filtradas por tipo
        const preguntasFiltradas = nuevasFalladas.filter(pregunta => 
          pregunta.moduloId === 'comun' || pregunta.moduloId === type
        );
        setPreguntasFalladas(preguntasFiltradas);
        
        if (preguntaActual >= preguntasFiltradas.length) {
          setPreguntaActual(Math.max(0, preguntasFiltradas.length - 1));
        }
        
        setRespuestas({});
        
        if (preguntasFiltradas.length === 0 || preguntaActual === preguntasFalladas.length - 1) {
          setModoRepaso(true);
        }
      }, 500);
    }
  }, [preguntaActual, preguntasFalladas, type]);

  // Handle navigation to previous question
  const handlePreviousQuestion = useCallback(() => {
    if (preguntaActual > 0) {
      setPreguntaActual(prev => prev - 1);
      setRespuestas(prev => {
        const next = { ...prev };
        delete next[preguntaActual - 1];
        return next;
      });
    }
  }, [preguntaActual]);

  // Handle navigation to next question
  const handleNextQuestion = useCallback(() => {
    if (preguntaActual < preguntasFalladas.length - 1) {
      setPreguntaActual(prev => prev + 1);
      setRespuestas(prev => {
        const next = { ...prev };
        delete next[preguntaActual + 1];
        return next;
      });
    }
  }, [preguntaActual, preguntasFalladas.length]);

  // Handle skipping a question
  const handleSaltar = useCallback(() => {
    if (preguntaActual < preguntasFalladas.length - 1) {
      setPreguntaActual(prev => prev + 1);
      setRespuestas(prev => {
        const next = { ...prev };
        delete next[preguntaActual + 1];
        return next;
      });
    }
  }, [preguntaActual, preguntasFalladas.length]);

  // Handle clearing all failed questions
  const handleLimpiarFallos = useCallback(() => {
    localStorage.removeItem('preguntasFalladas');
    setPreguntasFalladas([]);
  }, []);

  // Handle completion
  const handleCompletion = useCallback(() => {
    navigate(`/${type}`);
  }, [navigate, type]);

  // Handle confirming abandoning the review
  const handleConfirmAbandon = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Handle restart review
  const handleRestartReview = useCallback(() => {
    if (preguntasFalladas.length === 0) {
      handleCompletion();
    } else {
      setPreguntaActual(0);
      setRespuestas({});
      setModoRepaso(false);
    }
  }, [preguntasFalladas.length, handleCompletion]);

  return {
    // State
    preguntasFalladas,
    preguntaActual,
    respuestas,
    modoRepaso,
    userStatus,
    showTrialLimitModal,
    showConfirmModal,
    
    // Computed values
    preguntaActualObj: preguntasFalladas[preguntaActual],
    respuestaActual: respuestas[preguntaActual],
    hasNextQuestion: preguntaActual < preguntasFalladas.length - 1,
    isLastQuestion: preguntaActual === preguntasFalladas.length - 1,
    isEmpty: preguntasFalladas.length === 0,
    
    // Actions
    setShowTrialLimitModal,
    setShowConfirmModal,
    handleRespuesta,
    handlePreviousQuestion,
    handleNextQuestion,
    handleSaltar,
    handleLimpiarFallos,
    handleCompletion,
    handleConfirmAbandon,
    handleRestartReview
  };
};

export default useReviewLogic;